import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { HttpErrorInterceptor, JwtInterceptor, LoaderInterceptor } from './shared/interceptors';
import { BrowserModule, Title } from '@angular/platform-browser';
import { ToastrModule } from 'ngx-toastr';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { LoaderService } from './shared/services/loader.service';
import { AuthGuard } from './shared/guards/auth.guard';
import { NoAuthGuard } from './shared/guards/no-auth.guard';
import { SetPageTitle } from './shared/utils';
import { provideRouter, Router, withComponentInputBinding, withHashLocation, withRouterConfig } from '@angular/router';
import { AuthService } from './shared/services';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_ROUTES } from './app-routing.module';
import * as Sentry from '@sentry/angular';

const appInitializer = (authenticationService: AuthService) => () =>
  new Promise((resolve) =>
    authenticationService.activeSession() ? authenticationService.refreshToken().subscribe().add(resolve) : resolve(false),
  );

Sentry.init({
  dsn: 'https://9a0d36121987f11862caa27c8b74f88e@o4509233238573056.ingest.us.sentry.io/4509233249386496',
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      autoInject: false,
      showBranding: true,
      isNameRequired: true,
      isEmailRequired: true,
      formTitle: 'Report an Issue',
      messagePlaceholder: 'Please describe the issue you are experiencing.',
      submitButtonLabel: 'Send Feedback',
    }),
  ],
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
});

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot({
        preventDuplicates: true,
      }),
      FormlyModule.forRoot({
        extras: { lazyRender: true },
      }),
      FormlyBootstrapModule,
    ),
    LoaderService,
    Title,
    AuthGuard,
    NoAuthGuard,
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: SetPageTitle, deps: [Router, Title], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    provideAppInitializer(() => {
      const initializerFn = appInitializer(inject(AuthService));
      return initializerFn();
    }),
    provideAnimations(),
    provideEnvironmentNgxMask(),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([JwtInterceptor])),
    provideRouter(APP_ROUTES, withComponentInputBinding(), withHashLocation(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
